import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

interface ListSkeletonProps {
  /** Number of skeleton rows */
  rows?: number;
  /** Show card-style skeletons instead of rows */
  variant?: "row" | "card";
  /** Additional class */
  className?: string;
}

/**
 * Reusable skeleton loader that preserves layout during data fetching.
 */
export function ListSkeleton({ rows = 5, variant = "row", className }: ListSkeletonProps) {
  if (variant === "card") {
    return (
      <div className={cn("space-y-4 p-4", className)} aria-label="Loading content" aria-live="polite" role="status">
        {Array.from({ length: rows }).map((_, i) => (
          <Skeleton key={i} className="h-32 w-full rounded-native-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className={cn("space-y-0", className)} aria-label="Loading content" aria-live="polite" role="status">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-3.5 border-b border-border/40">
          <Skeleton className="h-9 w-9 rounded-lg flex-shrink-0" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="h-4 w-4 rounded-full" />
        </div>
      ))}
    </div>
  );
}

interface PageSkeletonProps {
  className?: string;
}

/**
 * Full-page skeleton with header + cards + list for initial page loads.
 */
export function PageSkeleton({ className }: PageSkeletonProps) {
  return (
    <div className={cn("flex-1 space-y-6 p-4 animate-in fade-in duration-200", className)} aria-label="Loading page" aria-live="polite" role="status">
      {/* Header skeleton */}
      <div className="space-y-2">
        <Skeleton className="h-8 w-48 rounded-lg" />
        <Skeleton className="h-4 w-32 rounded-md" />
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3">
        <Skeleton className="h-24 rounded-native-lg" />
        <Skeleton className="h-24 rounded-native-lg" />
      </div>

      {/* List items */}
      <div className="space-y-0 overflow-hidden rounded-native-lg border bg-card shadow-card">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-3.5 border-b border-border/40 last:border-b-0">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-4 w-3/5" />
              <Skeleton className="h-3 w-2/5" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
